import { Order } from "./order";

/**
 * Parser for Peppol BIS 3.0 UBL Invoice/CreditNote XML documents
 */
export class UblXmlParser {
	private doc: Document;

	constructor(xmlString: string) {
		const parser = new DOMParser();
		this.doc = parser.parseFromString(xmlString, "text/xml");
	}

	/**
	 * Parse the XML document into an Order object
	 */
	public parse(): Order {
		const root = this.doc.documentElement;
		const isCreditNote = root.localName === "CreditNote";

		const allowanceCharges = this.parseAllowanceCharges();
		const cashDiscount = this.parseCashDiscount(allowanceCharges);

		const data: any = {
			Customer: this.parseCustomerParty(),
			Supplier: this.parseSupplierParty(),
			OrderNumber: this.getText("ID"),
			OrderDate: this.getDate("IssueDate"),
			ExpiryDate: this.getDate("DueDate"),
			OrderType: isCreditNote ? "CreditNote" : "Invoice",
			OrderLines: this.parseInvoiceLines(isCreditNote),
			VatGroups: this.parseVatGroups(),
			PaymentReference: this.getPaymentReference(),
			Reference: this.getOrderReference(),
			Comments: this.getText("Note"),
			Currency: this.getText("DocumentCurrencyCode"),
			BankAccount: this.parseBankAccount(),
			SenderEndpointID: this.getEndpointID("AccountingSupplierParty"),
			ReceiverEndpointID: this.getEndpointID("AccountingCustomerParty"),
			// Additional fields from XML
			PaymentTermsNote: this.getPaymentTermsNote(),
			AllowanceCharges: allowanceCharges,
			AdditionalDocumentReferences: this.parseAdditionalDocumentReferences(),
			// Cash discount fields
			CashDiscountPercent: cashDiscount.percent,
			CashDiscountAmount: cashDiscount.amount,
			CashDiscountDays: cashDiscount.days,
			NetDueDays: cashDiscount.netDueDays,
		};

		return new Order(data);
	}

	/**
	 * Parse cash discount from AllowanceCharges and PaymentTerms
	 */
	private parseCashDiscount(allowanceCharges: any[]): {
		percent: number;
		amount: number;
		days: number;
		netDueDays: number;
	} {
		// Find the cash discount allowance (ChargeIndicator = false, reason contains "cash" or "discount")
		const cashDiscountAllowance = allowanceCharges.find(ac =>
			!ac.ChargeIndicator &&
			(ac.AllowanceChargeReason?.toLowerCase().includes("cash") ||
			 ac.AllowanceChargeReason?.toLowerCase().includes("discount"))
		);

		const amount = cashDiscountAllowance?.Amount || 0;

		// Parse payment terms note to extract days and percentage
		// Example: "8 days -3% / 60 days due net"
		const paymentTermsNote = this.getPaymentTermsNote();
		let percent = 0;
		let days = 0;
		let netDueDays = 0;

		if (paymentTermsNote) {
			// Match patterns like "8 days -3%" or "8 days 3%"
			const discountMatch = paymentTermsNote.match(/(\d+)\s*days?\s*[-]?(\d+(?:[.,]\d+)?)\s*%/i);
			if (discountMatch) {
				days = parseInt(discountMatch[1], 10);
				percent = parseFloat(discountMatch[2].replace(",", "."));
			}

			// Match pattern like "60 days due net" or "60 days net"
			const netMatch = paymentTermsNote.match(/(\d+)\s*days?\s*(?:due\s*)?net/i);
			if (netMatch) {
				netDueDays = parseInt(netMatch[1], 10);
			}
		}

		return { percent, amount, days, netDueDays };
	}

	/**
	 * Get text content of an element by local name (ignoring namespace)
	 */
	private getText(localName: string, context?: Element): string {
		const parent = context || this.doc.documentElement;
		const element = this.findElementByLocalName(parent, localName);
		return element?.textContent?.trim() || "";
	}

	/**
	 * Get date from element
	 */
	private getDate(localName: string, context?: Element): Date | null {
		const text = this.getText(localName, context);
		if (!text) return null;
		return new Date(text);
	}

	/**
	 * Get number from element
	 */
	private getNumber(localName: string, context?: Element): number {
		const text = this.getText(localName, context);
		return parseFloat(text) || 0;
	}

	/**
	 * Find element by local name (namespace-agnostic)
	 */
	private findElementByLocalName(parent: Element, localName: string): Element | null {
		// First check direct children
		for (const child of Array.from(parent.children)) {
			if (child.localName === localName) {
				return child;
			}
		}
		// Then check all descendants
		const all = parent.getElementsByTagName("*");
		for (const el of Array.from(all)) {
			if (el.localName === localName) {
				return el;
			}
		}
		return null;
	}

	/**
	 * Find all elements by local name (namespace-agnostic)
	 */
	private findAllElementsByLocalName(parent: Element, localName: string): Element[] {
		const results: Element[] = [];
		const all = parent.getElementsByTagName("*");
		for (const el of Array.from(all)) {
			if (el.localName === localName) {
				results.push(el);
			}
		}
		return results;
	}

	/**
	 * Parse AccountingSupplierParty
	 */
	private parseSupplierParty(): any {
		const supplierParty = this.findElementByLocalName(this.doc.documentElement, "AccountingSupplierParty");
		if (!supplierParty) return {};

		const party = this.findElementByLocalName(supplierParty, "Party");
		if (!party) return {};

		return this.parseParty(party, "Supplier");
	}

	/**
	 * Parse AccountingCustomerParty
	 */
	private parseCustomerParty(): any {
		const customerParty = this.findElementByLocalName(this.doc.documentElement, "AccountingCustomerParty");
		if (!customerParty) return {};

		const party = this.findElementByLocalName(customerParty, "Party");
		if (!party) return {};

		return this.parseParty(party, "Customer");
	}

	/**
	 * Parse a Party element
	 */
	private parseParty(party: Element, partyType: string): any {
		const postalAddress = this.findElementByLocalName(party, "PostalAddress");
		const partyTaxScheme = this.findElementByLocalName(party, "PartyTaxScheme");
		const contact = this.findElementByLocalName(party, "Contact");
		const partyLegalEntity = this.findElementByLocalName(party, "PartyLegalEntity");

		return {
			Name: this.getText("Name", this.findElementByLocalName(party, "PartyName") || party)
				|| this.getText("RegistrationName", partyLegalEntity || party),
			Street: postalAddress ? this.getText("StreetName", postalAddress) : "",
			Zipcode: postalAddress ? this.getText("PostalZone", postalAddress) : "",
			City: postalAddress ? this.getText("CityName", postalAddress) : "",
			CountryCode: postalAddress ? this.getText("IdentificationCode", this.findElementByLocalName(postalAddress, "Country") || postalAddress) : "",
			VATNumber: partyTaxScheme ? this.getText("CompanyID", partyTaxScheme) : "",
			VATLiable: !!partyTaxScheme,
			Phone: contact ? this.getText("Telephone", contact) : "",
			Email: contact ? this.getText("ElectronicMail", contact) : "",
			Contact: contact ? this.getText("Name", contact) : "",
			EndpointID: this.getText("EndpointID", party),
			PartyType: partyType,
		};
	}

	/**
	 * Get EndpointID for a party
	 */
	private getEndpointID(partyElementName: string): string {
		const partyContainer = this.findElementByLocalName(this.doc.documentElement, partyElementName);
		if (!partyContainer) return "";
		const party = this.findElementByLocalName(partyContainer, "Party");
		if (!party) return "";
		return this.getText("EndpointID", party);
	}

	/**
	 * Parse InvoiceLine or CreditNoteLine elements
	 */
	// eslint-disable-next-line max-lines-per-function
	private parseInvoiceLines(isCreditNote: boolean): any[] {
		const lineName = isCreditNote ? "CreditNoteLine" : "InvoiceLine";
		const quantityName = isCreditNote ? "CreditedQuantity" : "InvoicedQuantity";
		const lines = this.findAllElementsByLocalName(this.doc.documentElement, lineName);

		// eslint-disable-next-line max-lines-per-function
		return lines.map(line => {
			const item = this.findElementByLocalName(line, "Item");
			const price = this.findElementByLocalName(line, "Price");
			const taxCategory = item ? this.findElementByLocalName(item, "ClassifiedTaxCategory") : null;
			const sellersItemId = item ? this.findElementByLocalName(item, "SellersItemIdentification") : null;

			const quantity = this.getNumber(quantityName, line);
			const lineAmount = this.getNumber("LineExtensionAmount", line);
			const unitPrice = price ? this.getNumber("PriceAmount", price) : (quantity ? lineAmount / quantity : 0);
			const vatPercent = taxCategory ? this.getNumber("Percent", taxCategory) : 0;
			const totalVat = lineAmount * (vatPercent / 100);

			// Parse all line-level AllowanceCharge elements (discounts and charges like recupel)
			const lineAllowanceCharges = Array.from(line.children).filter(c => c.localName === "AllowanceCharge");
			let reductionPercentage = 0;
			let reductionAmount = 0;
			const charges: any[] = [];
			for (const ac of lineAllowanceCharges) {
				const isCharge = this.getText("ChargeIndicator", ac) === "true";
				const amount = this.getNumber("Amount", ac);
				const reason = this.getText("AllowanceChargeReason", ac);
				if (isCharge) {
					charges.push({
						Reason: reason,
						Amount: amount,
					});
				} else if (reductionAmount === 0 && reductionPercentage === 0) {
					reductionAmount = amount;
					const multiplier = this.getNumber("MultiplierFactorNumeric", ac);
					if (multiplier > 0) {
						reductionPercentage = multiplier * 100;
					} else if (amount > 0 && unitPrice > 0 && quantity > 0) {
						reductionPercentage = (amount / (unitPrice * quantity)) * 100;
					}
				}
			}

			return {
				Quantity: quantity,
				UnitPriceExcl: unitPrice,
				Description: item ? (this.getText("Description", item) || this.getText("Name", item)) : "",
				Sku: sellersItemId ? this.getText("ID", sellersItemId) : "",
				TotalExcl: lineAmount,
				TotalVAT: totalVat,
				TotalIncl: lineAmount + totalVat,
				VATPercentage: vatPercent,
				ReductionPercentage: reductionPercentage,
				ReductionAmount: reductionAmount,
				TaxExempt: taxCategory ? this.getText("ID", taxCategory) === "E" : false,
				TaxExemptionReason: taxCategory ? this.getText("TaxExemptionReason", taxCategory) : "",
				IsComment: false,
				Charges: charges,
				// Additional line data from XML
				LineID: this.getText("ID", line),
				UnitCode: this.getAttributeFromElement(line, quantityName, "unitCode"),
			};
		});
	}

	/**
	 * Get attribute from a child element
	 */
	private getAttributeFromElement(parent: Element, elementName: string, attrName: string): string {
		const el = this.findElementByLocalName(parent, elementName);
		return el?.getAttribute(attrName) || "";
	}

	/**
	 * Parse TaxTotal/TaxSubtotal elements into VatGroups
	 */
	private parseVatGroups(): any[] {
		const taxTotal = this.findElementByLocalName(this.doc.documentElement, "TaxTotal");
		if (!taxTotal) return [];

		const subtotals = this.findAllElementsByLocalName(taxTotal, "TaxSubtotal");
		return subtotals.map(subtotal => {
			const taxCategory = this.findElementByLocalName(subtotal, "TaxCategory");
			const vatPercent = taxCategory ? this.getNumber("Percent", taxCategory) : 0;
			const taxableAmount = this.getNumber("TaxableAmount", subtotal);
			const taxAmount = this.getNumber("TaxAmount", subtotal);
			const taxCategoryId = taxCategory ? this.getText("ID", taxCategory) : "";

			return {
				VATPercentage: vatPercent,
				TotalExcl: taxableAmount,
				TotalVAT: taxAmount,
				TotalIncl: taxableAmount + taxAmount,
				RoundedTotal: taxableAmount,
				TaxExempt: taxCategoryId === "E" || taxCategoryId === "Z",
				TaxExemptionReasons: [],
				// Additional data from XML
				TaxCategoryID: taxCategoryId,
			};
		});
	}

	/**
	 * Parse PaymentMeans to get bank account
	 */
	private parseBankAccount(): any | null {
		const paymentMeans = this.findElementByLocalName(this.doc.documentElement, "PaymentMeans");
		if (!paymentMeans) return null;

		const payeeAccount = this.findElementByLocalName(paymentMeans, "PayeeFinancialAccount");
		if (!payeeAccount) return null;

		const branch = this.findElementByLocalName(payeeAccount, "FinancialInstitutionBranch");

		return {
			Iban: this.getText("ID", payeeAccount),
			Bic: branch ? this.getText("ID", branch) : "",
		};
	}

	/**
	 * Get payment reference from PaymentMeans or PaymentID
	 */
	private getPaymentReference(): string {
		// Try PaymentMeans/PaymentID first
		const paymentMeans = this.findElementByLocalName(this.doc.documentElement, "PaymentMeans");
		if (paymentMeans) {
			const paymentId = this.getText("PaymentID", paymentMeans);
			if (paymentId) return paymentId;
		}
		// Fall back to invoice ID
		return "";
	}

	/**
	 * Get order reference
	 */
	private getOrderReference(): string {
		const orderRef = this.findElementByLocalName(this.doc.documentElement, "OrderReference");
		if (!orderRef) return "";
		return this.getText("ID", orderRef);
	}

	/**
	 * Get payment terms note
	 */
	private getPaymentTermsNote(): string {
		const paymentTerms = this.findElementByLocalName(this.doc.documentElement, "PaymentTerms");
		if (!paymentTerms) return "";
		return this.getText("Note", paymentTerms);
	}

	/**
	 * Parse AllowanceCharge elements
	 */
	private parseAllowanceCharges(): any[] {
		const charges = this.findAllElementsByLocalName(this.doc.documentElement, "AllowanceCharge");
		// Filter to only document-level charges (not line-level)
		const documentLevelCharges = charges.filter(charge => {
			const parent = charge.parentElement;
			return parent?.localName !== "InvoiceLine" && parent?.localName !== "CreditNoteLine";
		});

		return documentLevelCharges.map(charge => {
			const taxCategory = this.findElementByLocalName(charge, "TaxCategory");
			return {
				ChargeIndicator: this.getText("ChargeIndicator", charge) === "true",
				AllowanceChargeReason: this.getText("AllowanceChargeReason", charge),
				AllowanceChargeReasonCode: this.getText("AllowanceChargeReasonCode", charge),
				Amount: this.getNumber("Amount", charge),
				TaxCategoryID: taxCategory ? this.getText("ID", taxCategory) : "",
				TaxPercent: taxCategory ? this.getNumber("Percent", taxCategory) : 0,
			};
		});
	}

	/**
	 * Parse AdditionalDocumentReference elements
	 */
	private parseAdditionalDocumentReferences(): any[] {
		const refs = this.findAllElementsByLocalName(this.doc.documentElement, "AdditionalDocumentReference");
		return refs.map(ref => ({
			ID: this.getText("ID", ref),
			DocumentDescription: this.getText("DocumentDescription", ref),
		}));
	}

	/**
	 * Get LegalMonetaryTotal values
	 */
	public getLegalMonetaryTotal(): {
		LineExtensionAmount: number;
		TaxExclusiveAmount: number;
		TaxInclusiveAmount: number;
		AllowanceTotalAmount: number;
		ChargeTotalAmount: number;
		PayableAmount: number;
		} {
		const total = this.findElementByLocalName(this.doc.documentElement, "LegalMonetaryTotal");
		if (!total) {
			return {
				LineExtensionAmount: 0,
				TaxExclusiveAmount: 0,
				TaxInclusiveAmount: 0,
				AllowanceTotalAmount: 0,
				ChargeTotalAmount: 0,
				PayableAmount: 0,
			};
		}

		return {
			LineExtensionAmount: this.getNumber("LineExtensionAmount", total),
			TaxExclusiveAmount: this.getNumber("TaxExclusiveAmount", total),
			TaxInclusiveAmount: this.getNumber("TaxInclusiveAmount", total),
			AllowanceTotalAmount: this.getNumber("AllowanceTotalAmount", total),
			ChargeTotalAmount: this.getNumber("ChargeTotalAmount", total),
			PayableAmount: this.getNumber("PayableAmount", total),
		};
	}
}

/**
 * Parse a Peppol UBL XML string into an Order object
 */
export function parseUblXml(xmlString: string): Order {
	const parser = new UblXmlParser(xmlString);
	return parser.parse();
}
